import {t} from "@lingui/core/macro";

import type {
    Conversation,
    CooldownStatus,
    HideChatOptions,
} from "@khan/guide-types";

import formatCooldownDate from "./format-cooldown-date.ts";
import {
    isGuideChatDisabled,
    isGuideChatDisabledForAssessment,
} from "./is-guide-chat-disabled.ts";
import strings from "./strings.ts";

const mapCooldownStatusToDisableMessage = (
    cooldownStatus: CooldownStatus | null,
): HideChatOptions => {
    switch (cooldownStatus?.cooldownType) {
        case "AntiCheat":
            return {
                type: "Cooldown",
                message: t`Sorry! I'm disabled while you're working on assessments (Quizzes, Unit tests, Course Challenges and Mastery Challenges). This is your chance to show what you know without my help.

Just start over, wait 5 minutes after closing, or finish any open assessments and I will be back to help you with your regular work.`,
            };
        case "TeacherDirected":
            return {
                type: "FocusMode",
                message: strings.chatDisabledFocusMode(
                    cooldownStatus.teacherName ?? "",
                    formatCooldownDate(cooldownStatus.expiresAt ?? new Date()),
                ),
            };
        default:
            return {
                type: "Default",
                message: t`Sorry, but I'm not available on this page. This is your chance to show what you know without my help. You've got this!`,
            };
    }
};

const getHideChatOptions = ({
    forceHideChatOptions,
    pathname,
    cooldownStatus,
    filteredConversation,
}: {
    forceHideChatOptions: HideChatOptions | undefined;
    pathname: string;
    cooldownStatus: CooldownStatus;
    filteredConversation: Conversation;
}): HideChatOptions | undefined => {
    // TODO (GL-1434) hoist hideChatInput into a single global source of truth.
    if (forceHideChatOptions) {
        return forceHideChatOptions;
    }

    if (isGuideChatDisabledForAssessment(pathname)) {
        return {
            type: "Default",
            message: t`Sorry, but I'm not available on this page. This is your chance to show what you know without my help. You've got this!`,
        };
    }

    if (isGuideChatDisabled(pathname)) {
        return {type: "ActivityWithoutChat", message: null};
    }

    if (cooldownStatus?.hasCooldown) {
        return mapCooldownStatusToDisableMessage(cooldownStatus);
    }

    /**
     * If the conversation only contains error and ui-only messages,
     * hide the chat input as we are in an undesirable conversation state.
     * This will happen if the initial messages fail to load, or we are
     * in an error state such as failing to resume a conversation.
     */
    const errorChatState = !filteredConversation.some(
        ({isError, uiOnly}) => !isError && !uiOnly,
    );

    if (errorChatState) {
        return {type: "Default", message: null};
    }

    return undefined;
};

export default getHideChatOptions;
